import { createFileRoute } from "@tanstack/react-router";
import type {
  EquipmentModelId,
  FermentationProcedureId,
  FermentationDashboardId,
} from "@brewbase/db/types";
import { toast } from "sonner";
import { trpc } from "@/hooks/trpc";
import { useCan } from "@/hooks/use-operator";
import { SimpleSelect } from "@/components/ui/simple-select";

export const Route = createFileRoute(
  "/fermentations/procedures/$procedure_id/dashboard",
)({ component: DashboardPage });
function DashboardPage() {
  const { procedure_id } = Route.useParams();
  const id = Number(procedure_id) as FermentationProcedureId;
  const canEdit = useCan("fermentation-dashboard:edit");
  const utils = trpc.useUtils();
  const { data: procedure } = trpc.fermentationProcedures.get.useQuery(id);
  const { data: dashboards = [] } = trpc.fermentationDashboards.list.useQuery({
    modelId: procedure?.fermentor_model_id as
      | EquipmentModelId
      | null
      | undefined,
  });
  const select = trpc.fermentationDashboards.selectProcedure.useMutation({
    onSuccess: () => {
      void utils.fermentationProcedures.get.invalidate(id);
      void utils.fermentationDashboards.resolve.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  if (!procedure) return <p className="p-4 text-sm">Loading…</p>;
  const selected = dashboards.find((d) => d.id === procedure.dashboard_id);
  return (
    <div className="p-4 space-y-4 max-w-3xl">
      <p className="text-sm">
        The default operating view for fermentations using this procedure.
        Improvements to this dashboard are shared with other procedures using
        it.
      </p>
      {canEdit ? (
        <SimpleSelect
          value={
            procedure.dashboard_id == null ? "" : String(procedure.dashboard_id)
          }
          options={[
            { value: "", label: "No default dashboard" },
            ...dashboards.map((d) => ({ value: String(d.id), label: d.name })),
          ]}
          disabled={select.isPending}
          onChange={(value) =>
            select.mutate({
              procedureId: id,
              dashboardId: value
                ? (Number(value) as FermentationDashboardId)
                : null,
            })
          }
        />
      ) : (
        <p className="text-sm">{selected?.name ?? "No default dashboard"}</p>
      )}
      {procedure.dashboard_id && (
        <a
          className="text-sm underline block"
          href={`/fermentations/dashboards/${procedure.dashboard_id}`}
        >
          Open shared dashboard
        </a>
      )}
      <a className="text-sm underline block" href="/fermentations/dashboards">
        Browse dashboards
      </a>
    </div>
  );
}
